// Nepali numerals mapping
const nepaliNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];

/**
 * Convert English numerals to Nepali numerals
 * @param {string|number} value - The value to convert
 * @returns {string} - Value with Nepali numerals
 */
export const toNepaliNumerals = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/[0-9]/g, (digit) => nepaliNumerals[parseInt(digit)]);
};

/**
 * Format number with locale-aware formatting
 * @param {number} num - The number to format
 * @param {string} language - Language code ('en' or 'ne')
 * @returns {string} - Formatted number
 */
export const formatNumber = (num, language = 'en') => {
  if (num === null || num === undefined) return language === 'ne' ? '०' : '0';
  
  const formatted = new Intl.NumberFormat('en-IN').format(num);
  
  if (language === 'ne') {
    return toNepaliNumerals(formatted);
  }
  return formatted;
};

/**
 * Format currency in NPR with locale support
 * @param {number} amount - The amount to format
 * @param {string} language - Language code ('en' or 'ne')
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, language = 'en') => {
  if (amount instanceof String) {
    amount = parseInt(amount);
  }
  if (amount === null || amount === undefined || isNaN(amount)) {
    return language === 'ne' ? 'रु ०' : 'NPR 0';
  }
  
  // Format with Indian numbering system (lakhs, crores)
  const formatted = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
  
  if (language === 'ne') {
    return `रु ${toNepaliNumerals(formatted)}`;
  }
  return `NPR ${formatted}`;
};

// Format date with locale support
export const formatDate = (dateString, language = 'en') => {
  if (!dateString) return '';
  const date = new Date(dateString);
  
  if (language === 'ne') {
    const formatted = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
    // Convert numbers to Nepali
    return toNepaliNumerals(formatted);
  }
  
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

// Format date and time with locale support
export const formatDateTime = (dateString, language = 'en') => {
  if (!dateString) return '';
  const date = new Date(dateString);
  
  if (language === 'ne') {
    const formatted = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
    return toNepaliNumerals(formatted);
  }
  
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

// Format phone number
export const formatPhoneNumber = (countryCode, phoneNumber) => {
  return `+${countryCode} ${phoneNumber}`;
};

/**
 * Calculate progress percentage with locale support
 * @param {number} raised - Amount raised
 * @param {number} goal - Goal amount
 * @param {string} language - Language code ('en' or 'ne')
 * @returns {string} - Formatted percentage
 */
export const calculateProgress = (raised, goal, language = 'en') => {
  if (!goal || goal === 0) return language === 'ne' ? '०' : '0';
  const progress = Math.min((raised / goal) * 100, 100).toFixed(1);
  
  if (language === 'ne') {
    return toNepaliNumerals(progress);
  }
  return progress;
};

// Truncate text
export const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Get initials from name
export const getInitials = (name) => {
  if (!name) return '';
  const parts = name.split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};
